import {useState, useMemo, useCallback, useEffect} from 'react';

import {isPublishingVersion} from 'notebook/version-history/VersionHistoryPanelUtil';
import {PublishingVersion, Version} from 'notebook/version-history/VersionHistoryTypes';
import {PublishableVersionStatus} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';

export interface UsePublishingVersionDropdowns {
  hasPublishingVersions: boolean;
  isPublishingVersionExpanded: (versionId: number) => boolean;
  togglePublishingVersion: (versionId: number) => void;
  shouldShowPublishingVersionToggle: (version: Version) => boolean;
  isVersionShown: (versionId: number) => boolean;
  expandAll: () => void;
  isExpandAllDisabled: boolean;
  collapseAll: () => void;
  isCollapseAllDisabled: boolean;
}

interface PublishingVersionToggle {
  isExpanded: boolean;
  isToggleForDraftVersion: boolean;
}

// map the publishingVersion's ID to PublishingVersionToggle
type PublishingVersionToggles = Record<number, PublishingVersionToggle>;

function getDraftPublishingVersionToggle(
  publishingVersionToggles: PublishingVersionToggles
): PublishingVersionToggle | null {
  for (const publishingVersionToggle of Object.values(publishingVersionToggles)) {
    if (publishingVersionToggle.isToggleForDraftVersion) {
      return publishingVersionToggle;
    }
  }
  // there doesn't always have to be a draftPublishingVersionToggle
  // i.e. if there is no draft version present, there is not draft version toggle
  return null;
}

const setExpandCollapseAllDisabled = (
  publishingVersionToggles: PublishingVersionToggles,
  setIsExpandAllDisabled: (isDisabled: boolean) => void,
  setIsCollapseAllDisabled: (isDisabled: boolean) => void
): void => {
  const isAllExpanded = Object.values(publishingVersionToggles).every(
    (versionToggle) => versionToggle.isExpanded
  );
  setIsExpandAllDisabled(isAllExpanded);
  const isAllCollapsed = Object.values(publishingVersionToggles).every(
    (versionToggle) => !versionToggle.isExpanded
  );
  setIsCollapseAllDisabled(isAllCollapsed);
};

/**
 * Provide state variables that control the expand/collapse of publishing version dropdowns
 * @param versions array of versions. Function assumes versions are sorted descending by modified time
 */
export function usePublishingVersionDropdowns(
  versions: Array<Version>,
  openVersionId?: number
): UsePublishingVersionDropdowns {
  const [isExpandAllDisabled, setIsExpandAllDisabled] = useState<boolean>(false);
  const [isCollapseAllDisabled, setIsCollapseAllDisabled] = useState<boolean>(false);

  const hasPublishingVersions = useMemo(
    () => versions.some((version) => version.publishableVersionInfo),
    [versions]
  );

  const publishingVersions: Array<PublishingVersion> = useMemo(
    () => versions.filter((version) => isPublishingVersion(version)),
    [versions]
  );
  const publishingVersionIds: Set<number> = useMemo(
    () => new Set(publishingVersions.map((version) => version.id)),
    [publishingVersions]
  );
  // map draft versions to publishing versions
  const draftVersionIdToPublishingVersionId: Record<number, number> = useMemo(() => {
    const tmp: Record<number, number> = {};
    if (publishingVersionIds.size === 0) {
      return tmp;
    }
    let publishingVersionId: number = versions[0].id;
    for (const version of versions) {
      if (isPublishingVersion(version)) {
        publishingVersionId = version.id;
      } else {
        tmp[version.id] = publishingVersionId;
      }
    }
    return tmp;
  }, [versions, publishingVersionIds]);

  const publishingVersionIdsWithDraftVersions: Set<number> = useMemo(() => {
    const tmp: Set<number> = new Set();
    for (const publishingVersionId of Object.values(draftVersionIdToPublishingVersionId)) {
      tmp.add(publishingVersionId);
    }
    return tmp;
  }, [draftVersionIdToPublishingVersionId]);

  const shouldShowPublishingVersionToggle = useCallback(
    (version: Version): boolean =>
      isPublishingVersion(version) &&
      (publishingVersionIdsWithDraftVersions.has(version.id) ||
        Boolean(version.publishableVersionInfo?.versionDescription)),
    [publishingVersionIdsWithDraftVersions]
  );

  // Initialze the toggle state to an empty object, which then gets populated in the useEffect below
  // This ensures that we always have the correct toggle state when the publishing versions change
  const [publishingVersionToggles, _setPublishingVersionToggles] = useState<PublishingVersionToggles>({});
  const setPublishingVersionToggles = useCallback(
    (updateToggles: (prevPublishingVersionToggles: PublishingVersionToggles) => PublishingVersionToggles) => {
      _setPublishingVersionToggles((prevPublishingVersionToggles) => {
        const newPublishingVersionToggles = updateToggles(prevPublishingVersionToggles);
        setExpandCollapseAllDisabled(
          newPublishingVersionToggles,
          setIsExpandAllDisabled,
          setIsCollapseAllDisabled
        );
        return newPublishingVersionToggles;
      });
    },
    [_setPublishingVersionToggles, setIsExpandAllDisabled, setIsCollapseAllDisabled]
  );

  useEffect(() => {
    setPublishingVersionToggles((prevPublishingVersionToggles) => {
      const prevDraftPublishingVersionToggle = getDraftPublishingVersionToggle(prevPublishingVersionToggles);
      const newPublishingVersionToggles = Object.fromEntries(
        publishingVersions
          .filter((version) => shouldShowPublishingVersionToggle(version))
          .map((version, i) => {
            const isToggleForDraftVersion =
              version.publishableVersionInfo.publishingStatus === PublishableVersionStatus.DRAFT;
            const isExpandedFromPreviousDraftVersionToggle =
              isToggleForDraftVersion && prevDraftPublishingVersionToggle?.isExpanded;

            // A publishing version toggle is expanded if:
            // 1. The previous draft version toggle was expanded and this is the current draft version
            // 2. The toggle for this specific version was expanded in the previous render
            // 3. The open version is a child draft of this publishing version
            const isExpanded =
              isExpandedFromPreviousDraftVersionToggle ||
              prevPublishingVersionToggles[version.id]?.isExpanded ||
              (openVersionId != null && draftVersionIdToPublishingVersionId[openVersionId] === version.id);

            const publishingVersionToggle: PublishingVersionToggle = {
              isExpanded,
              isToggleForDraftVersion,
            };
            return [version.id, publishingVersionToggle];
          })
      );
      return newPublishingVersionToggles;
    });
  }, [
    draftVersionIdToPublishingVersionId,
    openVersionId,
    publishingVersions,
    setPublishingVersionToggles,
    shouldShowPublishingVersionToggle,
  ]);

  const isPublishingVersionExpanded = useCallback(
    (versionId: number): boolean => publishingVersionToggles[versionId]?.isExpanded || false,
    [publishingVersionToggles]
  );

  const togglePublishingVersion = useCallback(
    (versionId: number): void => {
      setPublishingVersionToggles((prevPublishingVersionToggles) => {
        const previousVersionToggle = prevPublishingVersionToggles[versionId];
        if (!previousVersionToggle) {
          return prevPublishingVersionToggles;
        }
        const newPublishingVersionToggles = {
          ...prevPublishingVersionToggles,
          [versionId]: {
            ...previousVersionToggle,
            isExpanded: !previousVersionToggle.isExpanded,
          },
        };
        return newPublishingVersionToggles;
      });
    },
    [setPublishingVersionToggles]
  );

  const isVersionShown = (versionId: number): boolean => {
    // if no publishing versions, show all versions
    if (!hasPublishingVersions) {
      return true;
    }
    // if version is a publishing version, show it
    if (publishingVersionIds.has(versionId)) {
      return true;
    }
    // otherwise, determine if the publishingVersion of the draftVersion is expanded
    const publishingVersionId = draftVersionIdToPublishingVersionId[versionId];
    return isPublishingVersionExpanded(publishingVersionId);
  };

  const expandAll = useCallback((): void => {
    setPublishingVersionToggles((prevPublishingVersionToggles) =>
      Object.fromEntries(
        Object.entries(prevPublishingVersionToggles).map(([versionId, versionToggle]) => [
          versionId,
          {...versionToggle, isExpanded: true},
        ])
      )
    );
  }, [setPublishingVersionToggles]);

  const collapseAll = useCallback((): void => {
    setPublishingVersionToggles((prevPublishingVersionToggles) =>
      Object.fromEntries(
        Object.entries(prevPublishingVersionToggles).map(([versionId, versionToggle]) => [
          versionId,
          {...versionToggle, isExpanded: false},
        ])
      )
    );
  }, [setPublishingVersionToggles]);

  return {
    hasPublishingVersions,
    isPublishingVersionExpanded,
    togglePublishingVersion,
    shouldShowPublishingVersionToggle,
    isVersionShown,
    expandAll,
    isExpandAllDisabled,
    collapseAll,
    isCollapseAllDisabled,
  };
}
